// Country Toolkit Data for the 11 YEFFA Countries
// Tanzania, Rwanda, Malawi, Mali, Ghana, Burkina Faso, Mozambique, Uganda, Zambia, Ethiopia, Kenya

export const toolkitCountries = [
  {
    id: 'tanzania',
    name: 'Tanzania',
    flag: '🇹🇿',
    region: 'East Africa',
    overview: {
      youthInsights: 'Tanzania has over 65% of its population under 25. Agriculture employs 66% of the workforce. Youth are increasingly embracing commercial agriculture, particularly in horticulture and value-added processing.',
      valueChains: ['Cassava', 'Sunflower', 'Cashew', 'Horticulture', 'Coffee', 'Spices (Zanzibar)'],
      ecosystemNotes: 'Agricultural Sector Development Programme (ASDP) supports youth. Growing agritech scene in Dar es Salaam. Active presence of Farm Africa, AGRA, and other development partners.',
      keyStats: { youthPopulation: '65%', agricultureGDP: '28%', mobileUsers: '47%' }
    },
    storytelling: {
      localRefrains: [
        '"Kilimo kwanza" (Agriculture first)',
        '"Vijana wanaweza" (Youth can do it)',
        '"Kutoka shamba hadi soko" (From farm to market)'
      ],
      toneGuidance: 'Use Swahili phrases naturally throughout. Stories should emphasize the dignity and profitability of farming. Highlight value addition and processing. Connect individual success to community impact.',
      exampleStories: [
        { title: 'From Classroom to Cassava Farm', theme: 'Graduate builds thriving processing business' },
        { title: 'Sunflower Dreams', theme: 'Youth expands from small plot to regional supplier' },
        { title: 'Solar-Powered Irrigation Collective', theme: 'Youth group brings year-round farming to dry areas' }
      ]
    },
    implementation: {
      captionTemplates: [
        'Meet [Name], a [age]-year-old mkulima kutoka [Region] anayeonyesha #KilimoKwanza',
        'Safari ya [Name] kutoka [starting point] hadi [achievement] inahamasisha vijana',
        'Katika [Region], wakulima vijana kama [Name] wanabadilisha kilimo'
      ],
      interviewQuestions: [
        'Nini kilikufanya uchague kilimo?',
        'Umekuwa ukishinda changamoto gani?',
        'Una ushauri gani kwa vijana wengine?',
        'Unahitaji nini ili kukuza biashara yako?'
      ],
      photoGuidelines: 'Capture the diversity of Tanzanian agriculture - from coastal cashew to highland vegetables. Show processing and value addition in action. Include both individual farmers and group activities.'
    },
    activation: {
      sourcingStrategies: [
        'Partner with TAHA (Tanzania Horticulture Association)',
        'Connect with regional agricultural offices',
        'Work with MVIWATA (farmer network)',
        'Engage with agricultural colleges (SUA, etc.)',
        'Collaborate with Farm Africa and other NGOs'
      ],
      conversationTips: [
        'Begin with friendly Swahili greetings',
        'Allow time for relationship building',
        'Be prepared for group discussions',
        'Respect regional cultural differences',
        'Follow up through mobile phone calls'
      ],
      idealVenues: ['Processing facilities', 'Market centers', 'Cooperative offices', 'Farm demonstrations', 'Agricultural fairs']
    },
    measurement: {
      weeklyTargets: { stories: 3, photos: 15, videos: 2 },
      requiredMetrics: [
        'Regional coverage across Tanzania',
        'Value chain diversity',
        'Gender balance (target: 50%)',
        'Processing/value addition stories',
        'Engagement and reach metrics'
      ],
      qualityChecklist: [
        'Swahili language properly used',
        'Value addition angle captured',
        'Regional diversity represented',
        'Quality visuals collected',
        'Community impact documented'
      ]
    }
  },
  {
    id: 'rwanda',
    name: 'Rwanda',
    flag: '🇷🇼',
    region: 'East Africa',
    overview: {
      youthInsights: 'Rwanda has a young population with over 60% under 25. The government prioritizes agriculture modernization with strong policy support. Youth are increasingly attracted to high-value crops and agri-processing.',
      valueChains: ['Coffee', 'Tea', 'Horticulture', 'Mushrooms', 'Poultry', 'Dairy'],
      ecosystemNotes: 'Rwanda Agriculture Board (RAB) drives innovation. Strong ICT infrastructure supports agritech. Active youth programs including Hanga Umurimo. Development partners include USAID, FAO, and IFAD.',
      keyStats: { youthPopulation: '60%', agricultureGDP: '29%', mobileUsers: '75%' }
    },
    storytelling: {
      localRefrains: [
        '"Ubuhinzi ni ubuzima" (Agriculture is life)',
        '"Abana b\'u Rwanda bashobora" (Rwandan youth can do it)',
        '"Gutera imbere mu buhinzi" (Progressing through agriculture)'
      ],
      toneGuidance: 'Use Kinyarwanda phrases for authenticity. Narratives should emphasize innovation, efficiency, and modern approaches. Highlight circular economy concepts (waste-to-value). Focus on quality and value addition.',
      exampleStories: [
        { title: 'Mushrooms for the Future', theme: 'Coffee waste becomes profitable mushroom substrate' },
        { title: 'From Backyard to Export', theme: 'Mushroom enterprise scales to international markets' },
        { title: 'Smart Farming in Kigali', theme: 'Urban youth builds tech-enabled poultry business' }
      ]
    },
    implementation: {
      captionTemplates: [
        'Meet [Name], a [age]-year-old from [District] innovating in Rwandan agriculture',
        '[Name]\'s journey shows how youth are building sustainable agribusinesses',
        'In [District], young farmers like [Name] are leading agricultural transformation'
      ],
      interviewQuestions: [
        'What innovative approach sets your farming apart?',
        'How do you incorporate sustainability into your business?',
        'What role does technology play in your success?',
        'How do you see Rwandan agriculture evolving?'
      ],
      photoGuidelines: 'Capture innovation and modern techniques - greenhouses, processing equipment, quality control. Show urban and peri-urban farming. Include shots demonstrating efficiency and organization.'
    },
    activation: {
      sourcingStrategies: [
        'Partner with Rwanda Agriculture Board (RAB)',
        'Connect with district agronomists',
        'Work with Hanga Umurimo program',
        'Engage with agricultural cooperatives (INGABO, etc.)',
        'Collaborate with agritech startups and hubs'
      ],
      conversationTips: [
        'Emphasize innovation and forward-thinking approaches',
        'Be punctual - Rwandans value time management',
        'Focus on efficiency and quality elements',
        'Highlight scalability and sustainability',
        'Connect stories to national development goals'
      ],
      idealVenues: ['Innovation hubs', 'Processing facilities', 'Model farms', 'Cooperative offices', 'Agricultural shows']
    },
    measurement: {
      weeklyTargets: { stories: 3, photos: 15, videos: 2 },
      requiredMetrics: [
        'Innovation theme coverage',
        'Value addition stories proportion',
        'District diversity',
        'Gender balance',
        'Technology integration examples'
      ],
      qualityChecklist: [
        'Innovation clearly documented',
        'Scalability potential highlighted',
        'Quality visuals of modern techniques',
        'Sustainability elements captured',
        'Business model clearly explained'
      ]
    }
  },
  {
    id: 'malawi',
    name: 'Malawi',
    flag: '🇲🇼',
    region: 'Southern Africa',
    overview: {
      youthInsights: 'Malawi has one of the youngest populations in Africa with over 70% under 35. Agriculture accounts for 90% of export earnings, but youth often lack access to land and capital. Digital solutions are creating new opportunities for market access.',
      valueChains: ['Tobacco (transitioning)', 'Groundnuts', 'Maize', 'Tomatoes', 'Soybean', 'Aquaculture'],
      ecosystemNotes: 'Youth in Agribusiness (YiA) program is active. Strong cooperative movement. Growing agritech adoption with mobile-based market platforms. Development partners include USAID, EU, and FAO.',
      keyStats: { youthPopulation: '70%', agricultureGDP: '30%', mobileUsers: '42%' }
    },
    storytelling: {
      localRefrains: [
        '"Ulimi ndi moyo" (Farming is life)',
        '"Achinyamata atha" (Youth can do it)',
        '"Kupeza ndalama ku ntchito ya ulimi" (Finding money through farming)'
      ],
      toneGuidance: 'Use Chichewa phrases for authenticity. Stories should emphasize transformation - from traditional to commercial farming. Highlight digital/mobile solutions given infrastructure challenges. Focus on practical, achievable success.',
      exampleStories: [
        { title: 'Tomatoes in the Digital Age', theme: 'WhatsApp network connects farmers to urban markets' },
        { title: 'Digital Marketplace for Smallholders', theme: 'App connects 500+ farmers directly to buyers' },
        { title: 'Groundnut Gold', theme: 'Youth cooperative achieves premium prices through quality' }
      ]
    },
    implementation: {
      captionTemplates: [
        'Meet [Name], a [age]-year-old from [District] proving #UlimiNdiMoyo',
        '[Name]\'s story from [starting point] to [achievement] inspires Malawian youth',
        'In [District], young farmers like [Name] are changing the game'
      ],
      interviewQuestions: [
        'What made you see opportunity in agriculture?',
        'How has mobile technology changed how you farm or sell?',
        'What would you tell other young Malawians about farming?',
        'What support would help you grow your business?'
      ],
      photoGuidelines: 'Capture youth using mobile phones for farming/selling. Show market linkages in action. Include shots of quality produce and cooperative activities. Document both challenges and innovations.'
    },
    activation: {
      sourcingStrategies: [
        'Partner with Ministry of Agriculture district offices',
        'Connect with NASFAM (National Smallholder Farmers Association)',
        'Work with Youth in Agribusiness program',
        'Engage with mobile platform operators (Esoko, etc.)',
        'Collaborate with agricultural colleges'
      ],
      conversationTips: [
        'Begin with traditional greetings and introductions',
        'Be sensitive to land access challenges',
        'Highlight mobile/digital elements where present',
        'Allow time for group consultations',
        'Follow up through mobile messaging'
      ],
      idealVenues: ['NASFAM offices', 'Market days', 'Cooperative meetings', 'Training centers', 'Farm demonstrations']
    },
    measurement: {
      weeklyTargets: { stories: 2, photos: 12, videos: 1 },
      requiredMetrics: [
        'District coverage',
        'Mobile/digital story proportion',
        'Cooperative involvement rate',
        'Gender balance',
        'Story quality and engagement'
      ],
      qualityChecklist: [
        'Market linkage innovation captured',
        'Digital/mobile angle highlighted where relevant',
        'Cooperative benefits documented',
        'Quality photos and video collected',
        'Contact information for follow-up'
      ]
    }
  },
  {
    id: 'mali',
    name: 'Mali',
    flag: '🇲🇱',
    region: 'West Africa',
    overview: {
      youthInsights: 'Mali has over 65% of its population under 25. Agriculture accounts for 40% of GDP and employs 80% of the population. Youth face challenges with land access but are innovating in rice production and livestock.',
      valueChains: ['Rice', 'Cotton', 'Mango', 'Shea butter', 'Livestock', 'Millet/Sorghum'],
      ecosystemNotes: 'Office du Niger irrigation scheme is major agricultural zone. Active presence of USAID, FAO, and IFAD. Women\'s cooperatives are particularly strong in shea butter and rice processing.',
      keyStats: { youthPopulation: '65%', agricultureGDP: '40%', mobileUsers: '52%' }
    },
    storytelling: {
      localRefrains: [
        '"Sènèkèla ye baara nyuman ye" (Farming is good work)',
        '"Denmisɛnw bɛ se" (Youth can do it)',
        '"Mali ka malo" (Mali\'s rice/pride)'
      ],
      toneGuidance: 'Use French and Bambara appropriately. Stories should emphasize food sovereignty and local pride, especially around rice. Highlight women\'s leadership in cooperatives. Balance modern techniques with respect for tradition.',
      exampleStories: [
        { title: 'Rice Fields Revival', theme: 'Women\'s cooperative competes with imports through quality branding' },
        { title: 'Climate-Smart Rice Farming', theme: 'Youth introduces drought-resistant varieties' },
        { title: 'Malian Gold Rice', theme: 'Local rice brand gains market share from imports' }
      ]
    },
    implementation: {
      captionTemplates: [
        'Voici [Name], [age] ans, de [Region] qui transforme l\'agriculture malienne',
        'L\'histoire de [Name] montre ce qui est possible pour les jeunes dans l\'agriculture',
        'Dans [Region], les jeunes agriculteurs comme [Name] écrivent une nouvelle histoire'
      ],
      interviewQuestions: [
        'Qu\'est-ce qui vous a inspiré à rester dans l\'agriculture?',
        'Comment combinez-vous les méthodes traditionnelles et modernes?',
        'Quel conseil donneriez-vous aux autres jeunes?',
        'De quoi avez-vous besoin pour développer votre activité?'
      ],
      photoGuidelines: 'Capture the scale of rice production in the Niger delta. Show women\'s cooperative activities prominently. Include processing and packaging for quality rice. Document traditional and modern techniques.'
    },
    activation: {
      sourcingStrategies: [
        'Partner with Office du Niger administration',
        'Connect with women\'s cooperative federations',
        'Work with Ministry of Agriculture regional offices',
        'Engage with rice processor associations',
        'Collaborate with agricultural NGOs (ICRISAT, etc.)'
      ],
      conversationTips: [
        'Work with French and Bambara interpreters as needed',
        'Respect the prominent role of women in cooperatives',
        'Be sensitive to security situations in some regions',
        'Allow time for community consultation',
        'Acknowledge the importance of rice to national identity'
      ],
      idealVenues: ['Rice processing facilities', 'Cooperative offices', 'Irrigation scheme offices', 'Market centers', 'Women\'s group meetings']
    },
    measurement: {
      weeklyTargets: { stories: 2, photos: 12, videos: 1 },
      requiredMetrics: [
        'Women\'s story representation',
        'Rice value chain coverage',
        'Regional diversity (where accessible)',
        'Cooperative involvement rate',
        'Story quality and impact'
      ],
      qualityChecklist: [
        'French and Bambara properly captured',
        'Women\'s leadership highlighted',
        'Food sovereignty angle included',
        'Quality visuals of production and processing',
        'Community impact documented'
      ]
    }
  },
  {
    id: 'ghana',
    name: 'Ghana',
    flag: '🇬🇭',
    region: 'West Africa',
    overview: {
      youthInsights: 'Ghana\'s youth population (under 35) represents about 57% of the total. With increasing graduate unemployment, youth are rediscovering agriculture as a viable career. The "Planting for Food and Jobs" initiative has created new opportunities.',
      valueChains: ['Cocoa', 'Cashew', 'Poultry', 'Vegetables (tomatoes, peppers)', 'Rice', 'Beekeeping'],
      ecosystemNotes: 'Active agritech startup scene in Accra. Government programs like NABCO and Youth in Agriculture support young farmers. Strong presence of development partners including USAID, GIZ, and SNV.',
      keyStats: { youthPopulation: '57%', agricultureGDP: '20%', mobileUsers: '68%' }
    },
    storytelling: {
      localRefrains: [
        '"Agric be money" (Agriculture is money)',
        '"Small small, we dey grow" (Step by step, we\'re growing)',
        '"From farm to fame"'
      ],
      toneGuidance: 'Ghanaian youth appreciate stories of gradual success and community building. Use Twi phrases for authenticity. Emphasize cooperative models and youth groups. Balance aspiration with practical, achievable steps.',
      exampleStories: [
        { title: 'Sweet Success with Honey', theme: 'Beekeeping cooperative creates jobs across communities' },
        { title: 'Cassava Processing Revolution', theme: 'Youth-led factory transforms local value chain' },
        { title: 'The Tomato Queens', theme: 'Women\'s group conquers post-harvest losses' }
      ]
    },
    implementation: {
      captionTemplates: [
        'Meet [Name], a [age]-year-old from [Region] showing that #AgricBeMoney',
        '[Name]\'s journey from [starting point] to [achievement] - the Ghana youth agric story',
        'Young farmers for [Region] like [Name] dey change the agric game'
      ],
      interviewQuestions: [
        'What made you choose farming instead of waiting for white-collar jobs?',
        'How has being part of a cooperative helped your business?',
        'What advice would you give to young Ghanaians considering agriculture?',
        'What do you need most to take your farming to the next level?'
      ],
      photoGuidelines: 'Capture vibrant cooperative activities, processing facilities, and market scenes. Show both individual success and group collaboration. Include shots of modern equipment and traditional practices together.'
    },
    activation: {
      sourcingStrategies: [
        'Partner with MOFA district offices',
        'Connect with youth agricultural groups on social media',
        'Work with agricultural training institutes',
        'Engage with cooperative unions and FBOs',
        'Attend regional agricultural fairs and exhibitions'
      ],
      conversationTips: [
        'Start with informal conversation about family and community',
        'Acknowledge achievements before discussing challenges',
        'Offer to share stories through local radio if appropriate',
        'Be prepared for group interviews with cooperatives',
        'Follow up with WhatsApp updates on story publication'
      ],
      idealVenues: ['Cooperative meeting halls', 'Processing centers', 'Farm demonstrations', 'Agricultural shows', 'Youth entrepreneurship hubs']
    },
    measurement: {
      weeklyTargets: { stories: 3, photos: 15, videos: 2 },
      requiredMetrics: [
        'Stories per region',
        'Cooperative vs individual farmer ratio',
        'Gender balance (target: 50% women)',
        'Value chain coverage',
        'Story engagement and reach'
      ],
      qualityChecklist: [
        'Clear cooperative/community angle captured',
        'Both challenges and successes documented',
        'Quality media assets collected',
        'Regional diversity represented',
        'Youth voice and agency centered'
      ]
    }
  },
  {
    id: 'burkina-faso',
    name: 'Burkina Faso',
    flag: '🇧🇫',
    region: 'West Africa',
    overview: {
      youthInsights: 'Burkina Faso has over 65% of its population under 25. Agriculture employs 80% of the population but faces climate challenges. Youth are innovating in shea butter processing, livestock, and drought-resistant crops.',
      valueChains: ['Shea butter', 'Cotton', 'Sesame', 'Livestock', 'Mango', 'Cowpea'],
      ecosystemNotes: 'Strong women\'s shea cooperative network. Growing agritech adoption despite infrastructure challenges. Development partners include GIZ, USAID, and FAO. Climate adaptation is a key focus.',
      keyStats: { youthPopulation: '65%', agricultureGDP: '32%', mobileUsers: '46%' }
    },
    storytelling: {
      localRefrains: [
        '"Kogl-weogo" (Guardians of the bush - farmer protectors)',
        '"A san sida" (We can do it - Mooré)',
        '"De la terre, la richesse" (From the land, wealth)'
      ],
      toneGuidance: 'Use French with Mooré phrases for authenticity. Stories should emphasize resilience and climate adaptation. Highlight women\'s leadership, especially in shea butter. Balance challenges with hope and innovation.',
      exampleStories: [
        { title: 'Shea Butter Gold', theme: 'Women\'s cooperative achieves organic certification for export' },
        { title: 'Mobile Market Linkages', theme: 'SMS platform connects rural farmers to buyers' },
        { title: 'Climate-Smart Cowpea', theme: 'Youth introduces drought-resistant varieties' }
      ]
    },
    implementation: {
      captionTemplates: [
        'Voici [Name], [age] ans, de [Province] qui transforme l\'agriculture burkinabè',
        'L\'histoire de [Name] montre la résilience des jeunes agriculteurs',
        'Dans [Province], les jeunes comme [Name] innovent malgré les défis climatiques'
      ],
      interviewQuestions: [
        'Comment faites-vous face aux défis climatiques?',
        'Qu\'est-ce qui vous motive à continuer dans l\'agriculture?',
        'Comment la technologie a-t-elle changé votre façon de travailler?',
        'De quoi avez-vous besoin pour développer votre activité?'
      ],
      photoGuidelines: 'Capture shea nut collection and processing prominently. Show women\'s cooperative activities. Document climate adaptation techniques. Include both challenges and innovations in imagery.'
    },
    activation: {
      sourcingStrategies: [
        'Partner with women\'s shea cooperative networks',
        'Connect with Ministry of Agriculture provincial offices',
        'Work with climate adaptation programs',
        'Engage with agricultural NGOs (Tree Aid, etc.)',
        'Collaborate with microfinance institutions'
      ],
      conversationTips: [
        'Work with French and Mooré interpreters',
        'Be sensitive to security situations in some regions',
        'Highlight resilience and adaptation positively',
        'Respect the leadership role of women',
        'Allow time for community consultation'
      ],
      idealVenues: ['Shea processing centers', 'Cooperative offices', 'Market centers', 'Training facilities', 'Women\'s group meetings']
    },
    measurement: {
      weeklyTargets: { stories: 2, photos: 12, videos: 1 },
      requiredMetrics: [
        'Women\'s story representation',
        'Climate adaptation coverage',
        'Provincial diversity (where accessible)',
        'Shea value chain focus',
        'Story quality and impact'
      ],
      qualityChecklist: [
        'French and Mooré properly captured',
        'Climate resilience highlighted',
        'Women\'s leadership documented',
        'Quality visuals collected',
        'Community impact emphasized'
      ]
    }
  },
  {
    id: 'mozambique',
    name: 'Mozambique',
    flag: '🇲🇿',
    region: 'Southern Africa',
    overview: {
      youthInsights: 'Mozambique has a very young population with over 65% under 25. Agriculture employs 70% of the labor force but is largely subsistence-based. Youth are increasingly seeking commercial farming opportunities, especially in cashew and horticulture.',
      valueChains: ['Cashew', 'Cassava', 'Coconut', 'Horticulture', 'Poultry', 'Honey'],
      ecosystemNotes: 'Government\'s Sustenta program supports youth agriculture. Strong presence of international partners including IFAD, World Bank, and USAID. Growing agricultural investment in northern provinces.',
      keyStats: { youthPopulation: '65%', agricultureGDP: '28%', mobileUsers: '35%' }
    },
    storytelling: {
      localRefrains: [
        '"Agricultura é vida" (Agriculture is life)',
        '"Da terra vem o futuro" (From the land comes the future)',
        '"Jovens podem fazer" (Youth can do it)'
      ],
      toneGuidance: 'Use Portuguese for wider reach. Narratives should emphasize resilience and resourcefulness. Highlight how youth are modernizing traditional family farms. Include stories of climate adaptation given frequent natural disasters.',
      exampleStories: [
        { title: 'Cashew Renaissance', theme: 'Youth revives grandfather\'s cashew orchards with modern techniques' },
        { title: 'Organic Honey Value Chain', theme: 'Women beekeepers build export-ready business' },
        { title: 'From Subsistence to Sales', theme: 'Young farmer transforms small plot into vegetable supply business' }
      ]
    },
    implementation: {
      captionTemplates: [
        'Conheça [Name], um jovem de [Province] que está transformando a agricultura moçambicana',
        'A história de [Name] mostra o que é possível para os jovens no campo',
        'Em [Province], jovens agricultores como [Name] estão escrevendo um novo capítulo'
      ],
      interviewQuestions: [
        'O que te inspirou a continuar na agricultura?',
        'Como você combina métodos tradicionais com técnicas modernas?',
        'Que conselho daria a outros jovens moçambicanos?',
        'Quais são os maiores desafios que enfrenta?'
      ],
      photoGuidelines: 'Capture the diversity of Mozambican agriculture - from coastal coconut groves to inland cashew orchards. Show youth working with both traditional and improved methods. Include family and community contexts.'
    },
    activation: {
      sourcingStrategies: [
        'Partner with MASA (Ministry of Agriculture) provincial offices',
        'Connect with Sustenta program coordinators',
        'Work with cashew and honey cooperatives',
        'Engage with agricultural NGOs (CLUSA, CARE)',
        'Collaborate with rural youth organizations'
      ],
      conversationTips: [
        'Work with Portuguese translators for non-Portuguese speakers',
        'Be sensitive to recent climate disaster impacts',
        'Allow storytellers to share at their own pace',
        'Respect cultural protocols in rural communities',
        'Plan for longer travel times in remote areas'
      ],
      idealVenues: ['Cooperative offices', 'Processing centers', 'Farm visits', 'Community meetings', 'Agricultural fairs']
    },
    measurement: {
      weeklyTargets: { stories: 2, photos: 12, videos: 1 },
      requiredMetrics: [
        'Provincial coverage',
        'Value chain representation',
        'Gender balance',
        'Climate adaptation stories',
        'Story quality scores'
      ],
      qualityChecklist: [
        'Portuguese and local language properly captured',
        'Climate resilience elements documented',
        'Family/community context included',
        'Quality visual content collected',
        'Follow-up contact established'
      ]
    }
  },
  {
    id: 'uganda',
    name: 'Uganda',
    flag: '🇺🇬',
    region: 'East Africa',
    overview: {
      youthInsights: 'Uganda has one of the youngest populations globally with over 75% under 30. Agriculture employs 72% of the workforce. Youth are increasingly drawn to coffee value addition, dairy modernization, and urban horticulture.',
      valueChains: ['Coffee', 'Dairy', 'Maize', 'Bananas', 'Horticulture', 'Poultry'],
      ecosystemNotes: 'Operation Wealth Creation targets youth. Uganda Development Bank offers agricultural loans. Active presence of USAID, GIZ, and private sector agritech companies. Kampala has a growing agri-startup ecosystem.',
      keyStats: { youthPopulation: '75%', agricultureGDP: '24%', mobileUsers: '58%' }
    },
    storytelling: {
      localRefrains: ['"Obulimi bwe bwerere" (Agriculture is free enterprise)', '"Abaana ba Uganda basobola" (Ugandan youth can do it)', '"From garden to greatness"'],
      toneGuidance: 'Use English with Luganda phrases for cultural resonance. Stories should emphasize entrepreneurship and business thinking. Highlight coffee value chain innovations. Show both rural and urban youth agriculture success.',
      exampleStories: [
        { title: 'Beyond the Bean', theme: 'Youth builds premium coffee brand from family plot' },
        { title: 'Urban Dairy Revolution', theme: 'Young entrepreneur modernizes milk value chain' },
        { title: 'Banana Republic', theme: 'Value addition turns staple crop into export product' }
      ]
    },
    implementation: {
      captionTemplates: ['Meet [Name], a [age]-year-old from [District] transforming Ugandan agriculture', '[Name] proves that youth + agriculture = opportunity in Uganda', 'From [District], [Name] is building the future of farming'],
      interviewQuestions: ['What made you see agriculture as a business, not just farming?', 'How has your approach to farming changed from your parents\' generation?', 'What role does technology play in your daily work?', 'What would make the biggest difference for young farmers in Uganda?'],
      photoGuidelines: 'Capture coffee processing and quality grading. Show modern dairy facilities alongside traditional farming. Include urban farming innovations in Kampala. Document both large-scale and smallholder operations.'
    },
    activation: {
      sourcingStrategies: ['Partner with NAADS (National Agricultural Advisory Services)', 'Connect with Uganda Coffee Development Authority', 'Work with youth agricultural associations', 'Engage with Kampala agri-startup hubs', 'Collaborate with district production offices'],
      conversationTips: ['Start with warm, informal greetings', 'Ugandans appreciate humor and storytelling naturally', 'Allow farmers to demonstrate their work', 'Be ready for spontaneous community involvement', 'Follow up via WhatsApp and mobile calls'],
      idealVenues: ['Coffee washing stations', 'Dairy cooperatives', 'Farm demonstrations', 'Agricultural innovation hubs', 'Market centers']
    },
    measurement: {
      weeklyTargets: { stories: 3, photos: 15, videos: 2 },
      requiredMetrics: ['District coverage', 'Coffee value chain stories', 'Gender balance (target: 50%)', 'Urban vs rural representation', 'Story engagement metrics'],
      qualityChecklist: ['Entrepreneurship angle captured', 'Value addition documented', 'Quality visuals of farming operations', 'Youth voice and agency centered', 'Business impact quantified where possible']
    }
  },
  {
    id: 'zambia',
    name: 'Zambia',
    flag: '🇿🇲',
    region: 'Southern Africa',
    overview: {
      youthInsights: 'Zambia has over 65% of its population under 25. Agriculture employs about 50% of the labor force. Youth are exploring opportunities in aquaculture, horticulture, and value-added processing of traditional crops like groundnuts.',
      valueChains: ['Maize', 'Groundnuts', 'Soybean', 'Aquaculture', 'Horticulture', 'Honey'],
      ecosystemNotes: 'Farmer Input Support Programme (FISP) is a key government intervention. ZAMACE commodity exchange offers market access. Active partners include USAID, FAO, and World Vision. Growing interest in climate-smart agriculture.',
      keyStats: { youthPopulation: '65%', agricultureGDP: '20%', mobileUsers: '48%' }
    },
    storytelling: {
      localRefrains: ['"Ulimi ni moyo" (Farming is life)', '"Twende pamodzi" (Let us go together)', '"From seed to success"'],
      toneGuidance: 'Use English with Bemba or Nyanja phrases for local resonance. Stories should emphasize diversification beyond maize. Highlight aquaculture and horticulture innovations. Focus on youth overcoming land and capital access barriers.',
      exampleStories: [
        { title: 'Fish Farm Fortune', theme: 'Youth builds tilapia farm from backyard pond to commercial operation' },
        { title: 'Groundnut Processing Pioneer', theme: 'Young woman creates premium peanut butter brand' },
        { title: 'Smart Irrigation Collective', theme: 'Youth group pools resources for year-round vegetable production' }
      ]
    },
    implementation: {
      captionTemplates: ['Meet [Name], a [age]-year-old from [Province] showing Zambia\'s farming future', '[Name]\'s journey from [starting point] to [achievement] inspires Zambian youth', 'In [Province], young farmers like [Name] are diversifying agriculture'],
      interviewQuestions: ['What opportunity did you see that others missed?', 'How have you diversified beyond traditional crops?', 'What challenges do young farmers in Zambia face most?', 'What support would help you scale your operation?'],
      photoGuidelines: 'Capture aquaculture and fish farming operations. Show groundnut and soybean processing. Include diverse crop production beyond maize. Document cooperative activities and market access moments.'
    },
    activation: {
      sourcingStrategies: ['Partner with Ministry of Agriculture district offices', 'Connect with ZNFU (Zambia National Farmers Union)', 'Work with aquaculture development programs', 'Engage with agricultural cooperatives', 'Collaborate with youth enterprise development organizations'],
      conversationTips: ['Begin with respectful greetings appropriate to the region', 'Zambians appreciate patience and genuine interest', 'Ask about family and community connections', 'Be prepared for hospitality and sharing', 'Follow up through phone calls and community visits'],
      idealVenues: ['Fish farms and ponds', 'Cooperative offices', 'Market centers', 'Agricultural training institutes', 'Farm field days']
    },
    measurement: {
      weeklyTargets: { stories: 2, photos: 12, videos: 1 },
      requiredMetrics: ['Provincial coverage', 'Value chain diversification', 'Gender balance', 'Youth enterprise documentation', 'Story quality and reach'],
      qualityChecklist: ['Diversification angle captured', 'Youth enterprise clearly documented', 'Quality visuals of operations', 'Community benefit highlighted', 'Market access innovations shown']
    }
  },
  {
    id: 'ethiopia',
    name: 'Ethiopia',
    flag: '🇪🇹',
    region: 'East Africa',
    overview: {
      youthInsights: 'Ethiopia has over 70% of its population under 30. Agriculture accounts for 40% of GDP and 80% of employment. The country is undergoing rapid agricultural modernization with youth at the forefront of coffee, horticulture, and livestock innovation.',
      valueChains: ['Coffee', 'Sesame', 'Teff', 'Horticulture', 'Livestock', 'Honey'],
      ecosystemNotes: 'Agricultural Transformation Agency (ATA) drives modernization. Growing agritech sector in Addis Ababa. Partners include USAID, GIZ, and the Bill & Melinda Gates Foundation.',
      keyStats: { youthPopulation: '70%', agricultureGDP: '40%', mobileUsers: '45%' }
    },
    storytelling: {
      localRefrains: ['"Ersha new hiwot" (Agriculture is life - Amharic)', '"Lijoch yichalalu" (Youth can do it)', '"Ke irist ande wede nege" (From farm to tomorrow)'],
      toneGuidance: 'Use Amharic phrases for cultural depth. Stories should emphasize the heritage of Ethiopian agriculture (birthplace of coffee). Highlight innovation within tradition. Focus on quality and global competitiveness, especially in coffee.',
      exampleStories: [
        { title: 'Specialty Coffee Youth', theme: 'Young farmer achieves direct trade with European roasters' },
        { title: 'Teff Innovation', theme: 'Graduate develops gluten-free teff products for export' },
        { title: 'Highland Honey Enterprise', theme: 'Youth cooperative builds organic honey brand for premium markets' }
      ]
    },
    implementation: {
      captionTemplates: ['Meet [Name], a [age]-year-old from [Region] innovating in Ethiopian agriculture', '[Name]\'s story shows how Ethiopia\'s youth are building global agricultural brands', 'In [Region], young farmers like [Name] are writing the next chapter'],
      interviewQuestions: ['How does your work connect to Ethiopia\'s agricultural heritage?', 'What modern techniques have you adopted?', 'How do you see Ethiopian agriculture competing globally?', 'What support do young farmers need most?'],
      photoGuidelines: 'Capture the stunning landscapes of Ethiopian agriculture. Show coffee cherry picking, processing, and cupping. Document teff harvesting alongside modern methods. Include both highland and lowland farming contexts.'
    },
    activation: {
      sourcingStrategies: ['Partner with Agricultural Transformation Agency (ATA)', 'Connect with Ethiopian Commodity Exchange (ECX)', 'Work with coffee cooperatives and unions', 'Engage with agricultural universities (Haramaya, Hawassa)', 'Collaborate with youth agricultural associations'],
      conversationTips: ['Show respect for Ethiopian coffee culture and ceremony', 'Allow time for the traditional storytelling approach', 'Be mindful of regional and ethnic sensitivities', 'Highlight the pride in Ethiopian agricultural heritage', 'Follow up through local partners and phone calls'],
      idealVenues: ['Coffee cooperatives and washing stations', 'Agricultural research centers', 'Farm demonstrations', 'Commodity exchange offices', 'Agricultural universities']
    },
    measurement: {
      weeklyTargets: { stories: 3, photos: 15, videos: 2 },
      requiredMetrics: ['Regional coverage (Oromia, Amhara, SNNPR, etc.)', 'Coffee value chain stories', 'Gender balance', 'Innovation documentation', 'Global market readiness stories'],
      qualityChecklist: ['Cultural heritage connection made', 'Innovation clearly documented', 'Quality visuals of diverse landscapes', 'Youth entrepreneurship highlighted', 'Export/market readiness shown']
    }
  },
  {
    id: 'kenya',
    name: 'Kenya',
    flag: '🇰🇪',
    region: 'East Africa',
    overview: {
      youthInsights: 'Kenya has about 75% of its population under 35. Agriculture accounts for 34% of GDP. Kenya leads in East African agritech innovation with a vibrant startup ecosystem. Youth are particularly active in horticulture, dairy, and digital agriculture.',
      valueChains: ['Tea', 'Coffee', 'Horticulture (flowers, vegetables)', 'Dairy', 'Poultry', 'Avocado'],
      ecosystemNotes: 'iHub and other innovation hubs support agritech startups. M-Pesa mobile money revolutionized agricultural payments. Strong cooperative movement. Partners include USAID, DFID, and private sector agribusinesses.',
      keyStats: { youthPopulation: '75%', agricultureGDP: '34%', mobileUsers: '83%' }
    },
    storytelling: {
      localRefrains: ['"Kilimo ni biashara" (Agriculture is business)', '"Vijana wanaweza" (Youth can do it)', '"Hustler spirit" (Entrepreneurial drive)'],
      toneGuidance: 'Kenyan English with Swahili phrases works naturally. Stories should emphasize entrepreneurship, innovation, and market savvy. Highlight tech integration (mobile money, apps, precision farming). Show the business side of agriculture prominently.',
      exampleStories: [
        { title: 'Avocado Gold Rush', theme: 'Youth builds export-quality avocado operation' },
        { title: 'M-Farm Revolution', theme: 'Tech-savvy farmer uses data to optimize production and sales' },
        { title: 'Flower Power', theme: 'Young woman builds sustainable floriculture business' }
      ]
    },
    implementation: {
      captionTemplates: ['Meet [Name], a [age]-year-old from [County] disrupting Kenyan agriculture', '[Name] shows that farming is the ultimate hustle - from [County] to the world', 'In [County], young agripreneurs like [Name] are redefining what farming looks like'],
      interviewQuestions: ['How does technology change the way you farm?', 'What\'s your business model - how do you make farming profitable?', 'How do you use mobile money or apps in your daily operations?', 'What would you tell a university graduate considering agriculture?'],
      photoGuidelines: 'Capture tech-enabled farming - smartphones, sensors, data dashboards. Show modern greenhouse and dairy operations. Include vibrant market scenes and export-quality produce. Document both Nairobi agritech scene and rural farming.'
    },
    activation: {
      sourcingStrategies: ['Partner with county agricultural offices', 'Connect with agritech hubs (iHub, Nairobi Garage)', 'Work with Kenya National Farmers Federation', 'Engage with agricultural colleges (Egerton, JKUAT)', 'Attend agricultural innovation events and hackathons'],
      conversationTips: ['Kenyans are direct and business-minded - get to the point', 'Highlight the business opportunity in every story', 'Be prepared for tech-savvy farmers active on social media', 'Respect the diversity of Kenyan cultures and communities', 'Follow up via WhatsApp - it is the primary communication tool'],
      idealVenues: ['Agritech hubs and co-working spaces', 'Modern greenhouses and dairy farms', 'Agricultural shows (Nairobi ASK show)', 'County agricultural offices', 'Farmers markets and export facilities']
    },
    measurement: {
      weeklyTargets: { stories: 3, photos: 15, videos: 2 },
      requiredMetrics: ['County coverage', 'Tech integration stories', 'Gender balance', 'Export/market readiness', 'Story viral potential and engagement'],
      qualityChecklist: ['Business model clearly explained', 'Technology integration documented', 'Quality visuals of modern operations', 'Market/export angle captured', 'Youth entrepreneurship energy conveyed']
    }
  }
];

// Get toolkit by country ID
export const getToolkitByCountryId = (countryId) => {
  return toolkitCountries.find(c => c.id === countryId);
};

// Get all toolkit countries for navigation
export const getAllToolkitCountries = () => {
  return toolkitCountries.map(c => ({
    id: c.id,
    name: c.name,
    flag: c.flag,
    region: c.region
  }));
};
